import { useState, useRef, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { MessageCircleQuestion, X, Send, Loader2, Bot, BookOpen } from "lucide-react";

export default function SupportWidget() {
  const { lang } = useLang();
  const { pathname } = useLocation();
  const es = lang === "es";
  const [open, setOpen] = useState(false);
  const [sid, setSid] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, loading, open]);
  useEffect(() => {
    if (open && msgs.length === 0) {
      setMsgs([{ role: "assistant", content: es ? "¡Hola! Soy el asistente de URBIS. ¿En qué te ayudo?" : "Hi! I'm the URBIS assistant. How can I help?" }]);
    }
  }, [open, es, msgs.length]);

  // Hide on the dedicated support page and public invoice / project pages to avoid overlap.
  if (pathname === "/support" || pathname.startsWith("/pay/") || pathname.startsWith("/p/") || pathname.startsWith("/chat/")) return null;

  const send = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/support/chat", { message: q, session_id: sid, lang });
      setSid(data.session_id);
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMsgs((m) => [...m, { role: "assistant", content: formatApiErrorDetail(err.response?.data?.detail) }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} className="fixed bottom-5 right-5 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-primary text-white shadow-lg transition-transform hover:scale-105 hover:bg-orange-600" aria-label="Support" data-testid="support-widget-btn">
          <MessageCircleQuestion className="h-7 w-7" />
        </button>
      )}
      {open && (
        <div className="fixed bottom-5 right-5 z-[60] flex h-[520px] max-h-[80vh] w-[92vw] max-w-sm flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl" data-testid="support-widget-panel">
          <div className="flex items-center justify-between bg-secondary px-4 py-3 text-white">
            <span className="flex items-center gap-2 font-head font-bold"><Bot className="h-5 w-5 text-primary" /> {es ? "Asistente URBIS" : "URBIS Assistant"}</span>
            <button onClick={() => setOpen(false)} className="text-slate-300 hover:text-white" data-testid="support-widget-close"><X className="h-5 w-5" /></button>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="support-widget-messages">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-sm ${m.role === "user" ? "bg-primary text-white" : "bg-slate-100 text-slate-700"}`}>{m.content}</div>
              </div>
            ))}
            {loading && <div className="flex justify-start"><div className="rounded-2xl bg-slate-100 px-3.5 py-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /></div></div>}
            <div ref={endRef} />
          </div>
          <div className="flex items-center gap-1 border-t border-slate-100 px-3 py-1.5 text-xs">
            <Link to="/manual" onClick={() => setOpen(false)} className="inline-flex items-center gap-1 text-slate-400 hover:text-primary" data-testid="support-widget-manual"><BookOpen className="h-3.5 w-3.5" /> {es ? "Manual" : "Manual"}</Link>
            <span className="text-slate-300">·</span>
            <a href="mailto:support@urbis.shop" className="text-slate-400 hover:text-primary">support@urbis.shop</a>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-slate-100 p-3">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={es ? "Escribe tu pregunta…" : "Type your question…"} className="flex-1 rounded-sm border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary" data-testid="support-widget-input" />
            <button type="submit" disabled={loading || !input.trim()} className="flex h-9 w-9 items-center justify-center rounded-sm bg-primary text-white hover:bg-orange-600 disabled:opacity-50" data-testid="support-widget-send"><Send className="h-4 w-4" /></button>
          </form>
        </div>
      )}
    </>
  );
}
